import React, { useState } from "react";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import { usePadding } from "@/components/utils/Padding";
import { SlideDown } from "react-slidedown";
import "react-slidedown/lib/slidedown.css";
import "./styles.scss";

/* #META
 {
  "NAME": "Descriptive",
  "CLASSNAME": "descriptive-component",
  "URL": "http://nicasource.local"
 }
#ENDMETA */

const Descriptive = ({ data }) => {
  const descriptiveModule = data?.descriptive_module;
  if (!descriptiveModule) return null;

  const lowerContent = descriptiveModule?.content;
  const paddingComponent = descriptiveModule?.content?.padding_component;
  const titleColor = descriptiveModule?.content?.title_color;
  const benefits = lowerContent?.benefits || [];

  // State for desktop tabs
  const [activeTab, setActiveTab] = useState(0);
  // State for mobile dropdowns
  const [openBenefits, setOpenBenefits] = useState({});

  const halfLength = Math.ceil(benefits.length / 2);

  const toggleBenefit = (index) => {
    setOpenBenefits((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const paddingStyle = usePadding(paddingComponent);

  return (
    <section className="descriptive-component" style={paddingStyle}>
      <div className="container-revamp">
        {/* content */}
        <div className="descriptive-lower">
          {/* Desktop: Tabbed Interface */}
          <div className="benefits-section-desktop">
            <div className="tabs-container">
              <div className="tabs-header">
                <div className="tabs-column tabs-left">
                  {benefits.slice(0, halfLength).map((benefit, index) => (
                    <button
                      key={index}
                      className={`tab-button ${activeTab === index ? "active" : ""}`}
                      onClick={() => setActiveTab(index)}
                    >
                      <h4 className={`card-title--${titleColor}`}>{benefit?.title}</h4>

                      {activeTab === index ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="25"
                          viewBox="0 0 24 25"
                          fill="none"
                        >
                          <path
                            d="M1 12.5C1 12.5 5 4.5 12 4.5C19 4.5 23 12.5 23 12.5C23 12.5 19 20.5 12 20.5C5 20.5 1 12.5 1 12.5Z"
                            stroke="#FDE5AC"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 15.5C13.6569 15.5 15 14.1569 15 12.5C15 10.8431 13.6569 9.5 12 9.5C10.3431 9.5 9 10.8431 9 12.5C9 14.1569 10.3431 15.5 12 15.5Z"
                            stroke="#FDE5AC"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg
                          width="24"
                          height="25"
                          viewBox="0 0 24 25"
                          xmlns="http://www.w3.org/2000/svg"
                          stroke="#FDE5AC"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                        >
                          <path d="M3 10a13.358 13.358 0 0 0 3 2.685M21 10a13.358 13.358 0 0 1-3 2.685m-8 1.624L9.5 16.5m.5-2.19a10.59 10.59 0 0 0 4 0m-4 0a11.275 11.275 0 0 1-4-1.625m8 1.624.5 2.191m-.5-2.19a11.275 11.275 0 0 0 4-1.625m0 0 1.5 1.815M6 12.685 4.5 14.5" />
                          s
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
                <div className="tabs-column tabs-right">
                  {benefits.slice(halfLength).map((benefit, index) => {
                    const adjustedIndex = index + halfLength;
                    return (
                      <button
                        key={adjustedIndex}
                        className={`tab-button ${activeTab === adjustedIndex ? "active" : ""}`}
                        onClick={() => setActiveTab(adjustedIndex)}
                      >
                        <h4 className={`card-title--${titleColor}`}>{benefit?.title}</h4>
                        {activeTab === adjustedIndex ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="25"
                            viewBox="0 0 24 25"
                            fill="none"
                          >
                            <path
                              d="M1 12.5C1 12.5 5 4.5 12 4.5C19 4.5 23 12.5 23 12.5C23 12.5 19 20.5 12 20.5C5 20.5 1 12.5 1 12.5Z"
                              stroke="#FDE5AC"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M12 15.5C13.6569 15.5 15 14.1569 15 12.5C15 10.8431 13.6569 9.5 12 9.5C10.3431 9.5 9 10.8431 9 12.5C9 14.1569 10.3431 15.5 12 15.5Z"
                              stroke="#FDE5AC"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <svg
                            width="24"
                            height="25"
                            viewBox="0 0 24 25"
                            xmlns="http://www.w3.org/2000/svg"
                            stroke="#FDE5AC"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fill="none"
                          >
                            <path d="M3 10a13.358 13.358 0 0 0 3 2.685M21 10a13.358 13.358 0 0 1-3 2.685m-8 1.624L9.5 16.5m.5-2.19a10.59 10.59 0 0 0 4 0m-4 0a11.275 11.275 0 0 1-4-1.625m8 1.624.5 2.191m-.5-2.19a11.275 11.275 0 0 0 4-1.625m0 0 1.5 1.815M6 12.685 4.5 14.5" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="tabs-content">
                <div className="tabs-content-single">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeTab}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    >
                      {benefits[activeTab]?.title && (
                        <h4 className={`tab-title card-title--${titleColor}`}>
                          {benefits[activeTab]?.title}
                        </h4>
                      )}
                      {benefits[activeTab] && (
                        <div
                          className="tab-content"
                          dangerouslySetInnerHTML={{ __html: benefits[activeTab]?.text_content }}
                        />
                      )}
                      {benefits[activeTab]?.image?.url && (
                        <div className="tab-image">
                          <img
                            src={benefits[activeTab].image.url}
                            alt={
                              benefits[activeTab].image.alt || benefits[activeTab].image.title || ""
                            }
                          />
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile: Dropdown Interface */}
          <div className="benefits-section-mobile">
            {benefits && benefits.length > 0 && (
              <div className="descriptive-benefits">
                <div className="descriptive-column">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="benefit-card">
                      <div className="benefit-card-header">
                        {benefit?.title && (
                          <h4 className={`benefit-title card-title--${titleColor}`}>
                            {benefit?.title}
                          </h4>
                        )}
                        <button
                          onClick={() => toggleBenefit(index)}
                          className="benefit-toggle-button"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="25"
                            height="25"
                            viewBox="0 0 25 25"
                            fill="none"
                            style={{
                              marginLeft: "8px",
                              transition: "transform 0.3s",
                              transform: openBenefits[index] ? "rotate(180deg)" : "rotate(0deg)",
                            }}
                          >
                            <path
                              d="M6.3335 9.5L12.3335 15.5L18.3335 9.5"
                              stroke="#FDE5AC"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                      <SlideDown>
                        {openBenefits[index] && (
                          <div
                            className="benefit-card-content"
                            dangerouslySetInnerHTML={{ __html: benefit?.text_content }}
                          />
                        )}
                      </SlideDown>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

Descriptive.propTypes = {
  data: PropTypes.shape({
    acf_fc_layout: PropTypes.string,
    descriptive_module: PropTypes.shape({
      content: PropTypes.shape({
        padding_component: PropTypes.oneOfType([PropTypes.bool, PropTypes.any]),
        benefits: PropTypes.arrayOf(
          PropTypes.shape({
            title: PropTypes.string,
            text_content: PropTypes.string,
            image: PropTypes.shape({
              url: PropTypes.string,
              alt: PropTypes.string,
              title: PropTypes.string,
            }),
          })
        ),
      }),
    }),
  }),
};

export default Descriptive;
